/* =====================================================================
   Alex & Lace — entrance curtain ORCHESTRATION (hero score P0).

   The curtain ELEMENT + covering + the AL_ENTRANCE promise are set up by the
   early inline script in snippets/al-hero-curtain.liquid (runs before paint).
   This asset runs the READY-GATE and the synchronized dismissal:

     gate  = fonts.ready + LEAD image decode (hero image on the homepage / collection band
             on collection pages — whichever exists) + split built + ScrollTrigger bound
     hold  = min 600ms desktop / 350ms mobile (a too-fast flash reads like a bug)
     cap   = 2500ms FAIL-OPEN (content must appear even if a gate hangs)
     wipe  = curtain scaleY 1→0 (CSS) + LEAD image zoom-out (GSAP, desktop): hero 1.15→1, OR the
             collection band 1.08→1 (§5.8 heroZoom) — same zoom-out language, whichever exists
             + cascade release (~200ms into the wipe, via window.__alEntranceResolve)

   Deliberate improvement over escape.cafe's fixed 1.4s timer: we lift when the
   page is actually ready, not on a guess — while still covering 100% of init.
   ===================================================================== */

(() => {
  const de = document.documentElement;
  // Not armed → reduced-motion / JS-off path already chose "no curtain". Nothing to do.
  if (!de.classList.contains('al-curtain-armed')) return;
  // bfcache already force-dismissed it.
  if (de.classList.contains('al-curtain-done')) return;

  const isMobile = matchMedia('(max-width: 1023px)').matches;
  // Min-hold from the brand MOTION TOKENS (punch item 3): desktop = --al-dur-slow (600ms, entrances),
  // mobile = --al-dur-base (320ms, standard) — was an off-token 350ms. Read live so they stay in sync.
  const durToken = (name, fallback) => {
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      if (v.endsWith('ms')) return parseFloat(v);
      if (v.endsWith('s')) return parseFloat(v) * 1000;
    } catch (e) {}
    return fallback;
  };
  const MIN_HOLD = isMobile ? durToken('--al-dur-base', 320) : durToken('--al-dur-slow', 600);
  const CAP = 2500;
  const t0 = (window.performance && performance.now) ? performance.now() : Date.now();
  const now = () => ((window.performance && performance.now) ? performance.now() : Date.now());

  // ---- Gate promises -------------------------------------------------------
  const fontsReady = () =>
    (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();

  // Decode-gate the LEAD image so the curtain never lifts onto an undecoded / progressively-painting
  // image. `decodeGate(sel)` resolves when that image is ready (or instantly if it isn't on this page).
  const decodeGate = (sel) => {
    const img = document.querySelector(sel);
    if (!img) return Promise.resolve();
    if (img.decode) return img.decode().catch(() => {});
    if (img.complete) return Promise.resolve();
    return new Promise((res) => {
      img.addEventListener('load', res, { once: true });
      img.addEventListener('error', res, { once: true });
    });
  };
  const heroDecoded = () => decodeGate('[data-al-hero-img] img');
  const bandDecoded = () => decodeGate('[data-al-collection-band] img'); // collection pages only

  const splitReady = () =>
    (window.AL_SPLIT_READY && typeof window.AL_SPLIT_READY.then === 'function')
      ? window.AL_SPLIT_READY : Promise.resolve();

  const scrollTriggerReady = () =>
    (window.AL_ST_READY && typeof window.AL_ST_READY.then === 'function')
      ? window.AL_ST_READY : Promise.resolve();

  // ---- Dismissal -----------------------------------------------------------
  let dismissed = false;

  const releaseCascade = () => {
    if (window.__alEntranceResolve) { window.__alEntranceResolve(); window.__alEntranceResolve = null; }
  };

  const zoomOutHeroImage = () => {
    if (isMobile) return; // zoom-out omitted on mobile (score)
    const media = document.querySelector('[data-al-hero-img]');
    const gsap = window.gsap;
    if (!media || !gsap) return;
    // scale is the ONLY prop we touch here; the parallax tween animates y on the same element,
    // so the two compose without overwriting each other (different properties, no overwrite).
    gsap.fromTo(media, { scale: 1.15 }, { scale: 1, duration: 1.4, ease: 'power2.out' });
  };

  // Collection band load-zoom (§5.8, design heroZoom: Collection.dc.html) — the SAME entrance
  // zoom-out language as the hero image, gentler magnitude. Desktop-only (matches the hero
  // precedent; mobile renders the static final state). Scales the IMG (absolute, inset:0) inside the
  // band's overflow:hidden clip box — scale is always >= 1 so object-fit:cover still fills (no exposed
  // edges at any frame), and transform-only means zero CLS. No-op on non-collection pages.
  //
  // TIMING vs the hero: the curtain wipes UPWARD (reveals bottom-first), so the top-anchored band is
  // uncovered only in the LATER part of the wipe. Running the settle from the START of the wipe (as the
  // full-viewport hero can) would spend it behind the sheet. But firing at the wipe's END (transitionend)
  // leaves a visible PAUSE — the wipe finishes, the page sits, THEN the band settles: two gestures.
  //
  // So HOLD the zoomed-in start (1.08) while covered (set synchronously — the band is never revealed at
  // its rest scale), then fire the 1.08→1 settle mid-wipe, exactly when the curtain's rising bottom edge
  // clears the band's MIDPOINT (the band's top half starting to uncover). Then the band is already
  // visibly settling as the last of the curtain leaves it — ONE continuous gesture.
  //
  // GEOMETRY (viewport-dependent): the curtain is scaleY'd from its top origin, so its live rendered
  // bottom edge (`curtain.getBoundingClientRect().bottom`) falls from ~viewport-height to 0 through the
  // wipe. The band is static, so we read its midpoint ONCE. Trigger when curtainBottom <= bandMid. The
  // uncover fraction (bandMid / viewportHeight) shifts with band height vs viewport — reading both live
  // per frame makes it self-adapting (no hardcoded fraction). rAF polls the CSS transition each frame.
  const zoomOutCollectionBand = () => {
    if (isMobile) return;
    const img = document.querySelector('[data-al-collection-band] img');
    const band = document.querySelector('[data-al-collection-band]');
    const curtain = document.querySelector('[data-al-curtain-el]');
    const gsap = window.gsap;
    if (!img || !band || !gsap) return;
    gsap.set(img, { scale: 1.08 }); // held zoomed-in while the curtain still covers the band
    let settled = false;
    const settle = () => {
      if (settled) return; settled = true;
      gsap.to(img, { scale: 1, duration: 1.6, ease: 'expo.out' });
    };
    if (curtain) {
      const bandRect = band.getBoundingClientRect();
      const bandMid = bandRect.top + bandRect.height / 2; // static; measured once
      const tick = () => {
        if (settled) return;
        // curtain fully gone (done) OR its rising edge has passed the band midpoint → settle now.
        if (de.classList.contains('al-curtain-done') || curtain.getBoundingClientRect().bottom <= bandMid) {
          settle(); return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
      curtain.addEventListener('transitionend', settle, { once: true }); // safety: never miss the uncover
    }
    setTimeout(settle, 1400); // last-resort cap (rAF/threshold normally fires first)
  };

  // FROST-GHOST FIX (2026-07-17): the frosted header bar (.al-frosted, backdrop-filter: blur) can
  // bake a STALE backdrop capture during the entrance's compositing churn (curtain wipe + hero zoom
  // + headline reveal all at once) — an intermittent white patch at a per-reload-random position.
  // Toggling backdrop-filter off for two frames forces the browser to re-sample the SETTLED backdrop,
  // flushing the stale capture. Runs only on the JS-on entrance path; reduced-motion / JS-off never
  // churn the backdrop, so their frost is already correct (stays on).
  const repaintFrost = () => {
    const frosted = document.querySelectorAll('.al-frosted');
    if (!frosted.length) return;
    // NUDGE the blur radius by a sub-perceptible amount (not to `none`) so the browser re-runs the
    // filter and re-samples the settled backdrop WITHOUT ever dropping the blur — no unblur flicker.
    // Restoring the inline value next frames re-applies the CSS blur (another fresh sample).
    frosted.forEach((el) => { el.style.backdropFilter = 'blur(12.6px)'; el.style.webkitBackdropFilter = 'blur(12.6px)'; });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      frosted.forEach((el) => { el.style.backdropFilter = ''; el.style.webkitBackdropFilter = ''; });
    }));
  };

  const startWipe = () => {
    if (de.classList.contains('al-curtain-done')) return;
    de.classList.add('al-curtain-dismiss');
    zoomOutHeroImage();
    zoomOutCollectionBand();
    // Cascade starts ~200ms into the wipe — headline emerges as the sheet clears it.
    setTimeout(releaseCascade, 200);
    // Cleanup once the wipe finishes (transitionend), with a failsafe.
    const el = document.querySelector('[data-al-curtain-el]');
    const cleanup = () => {
      de.classList.add('al-curtain-done'); de.classList.remove('al-curtain-armed');
      // Flush the frost twice: now (wipe done) and after the 1.4s hero zoom-out has fully settled,
      // so the re-sampled backdrop is the final, static one.
      repaintFrost();
      setTimeout(repaintFrost, 550);
    };
    if (el) el.addEventListener('transitionend', cleanup, { once: true });
    setTimeout(cleanup, 1400);
  };

  const dismiss = () => {
    if (dismissed || de.classList.contains('al-curtain-done')) return;
    dismissed = true;
    const wait = Math.max(0, MIN_HOLD - (now() - t0));
    setTimeout(startWipe, wait);
  };

  Promise.all([fontsReady(), heroDecoded(), bandDecoded(), splitReady(), scrollTriggerReady()]).then(dismiss);
  setTimeout(dismiss, CAP); // fail-open: never hold past the cap
})();
